import { supabase } from '../lib/supabase'
import { StudyModule } from '../types/database'
import { getCachedOrFetch, cacheData, invalidateCache } from '../utils/cacheUtils'

export const studyModulesService = {
  // Get all study modules for the current user
  async getStudyModules(): Promise<StudyModule[]> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      
      // If user is not authenticated, return empty array
      if (!userId) {
        return [];
      }
      
      // Use cache with userId as part of the key
      const cacheKey = `study_modules_${userId}`;
      
      return await getCachedOrFetch(
        cacheKey,
        async () => {
          const { data, error } = await supabase
            .from('study_modules')
            .select('*')
            .eq('user_id', userId)
            .order('updated_at', { ascending: false });

          if (error) {
            console.error('Error fetching study modules:', error);
            throw error;
          }

          return data || [];
        },
        10 * 60 * 1000 // 10 minutes cache TTL
      );
    } catch (error) {
      console.error('Error in getStudyModules:', error);
      return [];
    }
  },

  // Get a specific study module
  async getStudyModule(id: string): Promise<StudyModule | null> {
    try {
      // Use cache with module id as key
      const cacheKey = `study_module_${id}`;
      
      return await getCachedOrFetch(
        cacheKey,
        async () => {
          const { data, error } = await supabase
            .from('study_modules')
            .select('*')
            .eq('id', id)
            .single();

          if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
            console.error('Error fetching study module:', error);
            throw error;
          }

          return data;
        },
        15 * 60 * 1000 // 15 minutes cache TTL
      );
    } catch (error) {
      console.error('Error in getStudyModule:', error);
      return null;
    }
  },

  // Create a new study module
  async createStudyModule(module: Omit<StudyModule, 'id' | 'user_id' | 'created_at' | 'updated_at'>, userId: string): Promise<StudyModule> {
    const { data, error } = await supabase
      .from('study_modules')
      .insert([{ ...module, user_id: userId }])
      .select()
      .single();

    if (error) {
      console.error('Error creating study module:', error);
      throw error;
    }

    // Invalidate the modules list cache
    invalidateCache(`study_modules_${userId}`);
    
    // Cache the new module
    if (data) {
      cacheData(`study_module_${data.id}`, data);
    }

    return data;
  },

  // Update a study module
  async updateStudyModule(id: string, updates: Partial<StudyModule>): Promise<StudyModule> {
    const { data, error } = await supabase
      .from('study_modules')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating study module:', error);
      throw error;
    }

    // Update the cache for this module
    if (data) {
      cacheData(`study_module_${id}`, data);
      // Also invalidate the modules list cache
      if (data.user_id) {
        invalidateCache(`study_modules_${data.user_id}`);
      }
    }

    return data;
  },

  // Delete a study module
  async deleteStudyModule(id: string): Promise<void> {
    // Get the module first to know the user_id for cache invalidation
    const { data: module } = await supabase
      .from('study_modules')
      .select('user_id')
      .eq('id', id)
      .single();
      
    const { error } = await supabase
      .from('study_modules')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting study module:', error);
      throw error;
    }

    // Invalidate caches
    invalidateCache(`study_module_${id}`);
    if (module?.user_id) {
      invalidateCache(`study_modules_${module.user_id}`);
    }
  },

  // Update progress for a study module
  async updateProgress(id: string, progress: number): Promise<void> {
    const status = progress === 100 ? 'completed' : progress > 0 ? 'in-progress' : 'not-started';
    
    const { data, error } = await supabase
      .from('study_modules')
      .update({ progress, status })
      .eq('id', id)
      .select('user_id')
      .single();

    if (error) {
      console.error('Error updating progress:', error);
      throw error;
    }

    // Invalidate caches
    invalidateCache(`study_module_${id}`);
    if (data?.user_id) {
      invalidateCache(`study_modules_${data.user_id}`);
    }
  }
};